/**
 * Date Filter Context
 * Manages the selected date range filter across the application
 * Supports date range: June 1, 2025 - December 31, 2025
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';

// Date range constraints
// Use Date(year, month, day) constructor to create dates in local timezone
// (month is 0-indexed: 0=Jan, 5=June, 11=Dec)
export const DATE_RANGE = {
  minDate: new Date(2025, 5, 1),    // June 1, 2025
  maxDate: new Date(2025, 11, 31),  // December 31, 2025
  defaultStartDate: new Date(2025, 5, 1),   // June 1, 2025
  defaultEndDate: new Date(2025, 11, 15),   // December 15, 2025
  // Legacy: keep defaultDate for backward compatibility
  defaultDate: new Date(2025, 11, 15),      // December 15, 2025
};

interface DateFilterContextType {
  // New date range properties
  startDate: Date;
  endDate: Date;
  setDateRange: (start: Date, end: Date) => void;
  // Legacy single date (maps to endDate for backward compatibility)
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  // Constraints
  minDate: Date;
  maxDate: Date;
  // Utility functions
  formatDate: (date: Date) => string;
  formatDateRange: () => string;
  getMonthFromDate: (date: Date) => string;
}

const DateFilterContext = createContext<DateFilterContextType | undefined>(undefined);

export const DateFilterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [startDate, setStartDate] = useState<Date>(DATE_RANGE.defaultStartDate);
  const [endDate, setEndDate] = useState<Date>(DATE_RANGE.defaultEndDate);

  const clampDate = (date: Date): Date => {
    if (date < DATE_RANGE.minDate) return DATE_RANGE.minDate;
    if (date > DATE_RANGE.maxDate) return DATE_RANGE.maxDate;
    return date;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateShort = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateRange = (): string => {
    return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`;
  };

  // Get the month string (e.g., "2025-06") from a date for data lookup
  const getMonthFromDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const setDateRange = (start: Date, end: Date) => {
    const clampedStart = clampDate(start);
    const clampedEnd = clampDate(end);

    // Ensure start is before end
    if (clampedStart <= clampedEnd) {
      setStartDate(clampedStart);
      setEndDate(clampedEnd);
    } else {
      setStartDate(clampedEnd);
      setEndDate(clampedStart);
    }
  };

  // Legacy: setSelectedDate sets the end date (for backward compatibility)
  const handleSetSelectedDate = (date: Date) => {
    const clamped = clampDate(date);
    setEndDate(clamped);
  };

  return (
    <DateFilterContext.Provider
      value={{
        startDate,
        endDate,
        setDateRange,
        // Legacy compatibility: selectedDate maps to endDate
        selectedDate: endDate,
        setSelectedDate: handleSetSelectedDate,
        minDate: DATE_RANGE.minDate,
        maxDate: DATE_RANGE.maxDate,
        formatDate,
        formatDateRange,
        getMonthFromDate,
      }}
    >
      {children}
    </DateFilterContext.Provider>
  );
};

export const useDateFilter = (): DateFilterContextType => {
  const context = useContext(DateFilterContext);
  if (!context) {
    throw new Error('useDateFilter must be used within a DateFilterProvider');
  }
  return context;
};

export default DateFilterContext;
